import { createElement } from "react";
import {
  ReactElementFactory,
  ReactSurveyElementsWrapper,
  SurveyElementBase,
  SurveyElementErrors,
  SurveyModel,
} from "survey-react-ui";
import { CtaPanelModel } from "../../../../../models";
import React from "react";
import { CtaPanelBase } from "./cta-panel-base";
import cn from "../../../../../utils/cn";

export class CtaPanel extends CtaPanelBase {
  private hasBeenExpanded: boolean = false;

  public get panel(): CtaPanelModel {
    return this.panelBase as CtaPanelModel;
  }

  public get index(): any {
    return this.panelBase.getPropertyValue("index", "");
  }

  protected renderElement(): React.JSX.Element {
    const header = this.renderHeader();
    const errors = (
      <SurveyElementErrors
        element={this.panelBase}
        cssClasses={this.panelBase.cssClasses}
        creator={this.creator}
      />
    );
    const style = {
      paddingLeft: this.panel.innerPaddingLeft,
      display: this.panel.renderedIsExpanded ? undefined : "none",
    };
    let content: React.JSX.Element | null = null;
    if (this.panel.renderedIsExpanded) {
      // this.hasBeenExpanded = true;
      const rows: React.JSX.Element[] = this.renderRows(
        this.panelBase.cssClasses
      );
      const className: string = this.panelBase.cssClasses.panel.content;
      content = this.renderContent(style, rows, className);
    }
    const focusIn = () => {
      if (this.panelBase) (this.panelBase as CtaPanelModel).focusIn();
    };
    return (
      <div
        ref={this.rootRef}
        className={cn(
          (this.panelBase as CtaPanelModel).getContainerCss(),
          "!border-none !p-0"
        )}
        onFocus={focusIn}
        id={this.panelBase.id}
      >
        <div className="flex flex-col gap-5">
          {this.panel.showErrorsAbovePanel ? errors : null}
          <div>
            {header}
            {this.panel.showErrorsAbovePanel ? null : errors}
            {content}
          </div>
        </div>
      </div>
    );
  }

  protected renderHeader() {
    if (!this.panel.hasTitle && !this.panel.hasDescription) {
      return null;
    }

    return (
      <div className="flex flex-col gap-5">
        {this.renderIndex()}
        {this.renderTitle()}
        {this.renderDescription()}
      </div>
    );
  }

  private renderIndex() {
    return (
      <div className="flex justify-center items-start h-[30px] w-[46px] gap-2.5 rounded-[5px] bg-[#E7EEF6] px-1.5 py-[5px]">
        <span className="text-sm font-semibold text-gray-700">
          {this.index}
        </span>
      </div>
    );
  }

  protected wrapElement(element: React.JSX.Element): React.JSX.Element {
    const survey: SurveyModel = this.panel.survey as SurveyModel;
    let wrapper: React.JSX.Element | null = null;
    if (survey) {
      wrapper = ReactSurveyElementsWrapper.wrapElement(
        survey,
        element,
        this.panel
      );
    }
    return wrapper ?? element;
  }

  protected renderContent(
    style: any,
    rows: React.JSX.Element[],
    className: string
  ): React.JSX.Element {
    return (
      <div style={style} className={className} id={this.panel.contentId}>
        {rows}
      </div>
    );
  }

  protected renderTitle(): React.JSX.Element | null {
    if (!this.panelBase.title) return null;
    return (
      <div className="text-sm font-medium text-gray-700">
        {this.panelBase.isRequireTextBeforeTitle && (
          <>
            {this.renderRequireText(this.panelBase)}
            {this.renderSpaceSpan("req-sp")}
          </>
        )}
        <span>
          {SurveyElementBase.renderLocString(
            this.panelBase.locTitle,
            null,
            "q_title"
          )}
        </span>
        {this.panelBase.isRequireTextAfterTitle && (
          <>
            {this.renderSpaceSpan("req-sp")}
            {this.renderRequireText(this.panelBase)}
          </>
        )}
      </div>
    );
  }

  private renderSpaceSpan(key: any) {
    return (
      <span data-key={key} key={key}>
        &nbsp;
      </span>
    );
  }

  private renderRequireText(element: any): React.JSX.Element {
    return (
      <span
        data-key={"req-text"}
        key={"req-text"}
        className={element.cssRequiredText}
        aria-hidden={true}
      >
        {element.requiredText}
      </span>
    );
  }

  protected renderDescription(): React.JSX.Element | null {
    if (!this.panelBase.description) return null;
    const text = SurveyElementBase.renderLocString(
      this.panelBase.locDescription
    );
    return <div className="text-xs font-normal text-gray-700">{text}</div>;
  }
  protected getIsVisible(): boolean {
    return this.panelBase.getIsContentVisible();
  }
}

ReactElementFactory.Instance.registerElement("cta-panel", (props) => {
  return createElement(CtaPanel, props);
});
